import requestV2 from '@/admin/api/requestV2';
import request from '@/app/api/request';
import { CourtBigModel } from '@/app/api/courts/models';

export type AdminStatus = 'ACTIVE' | 'PENDING' | 'DECLINED';

export interface AdminCourtFields {
  isActive: boolean;
  adminStatus: AdminStatus;
  activeUsersCount: number;
  isDraft?: boolean;
}

export type AdminCourt = CourtBigModel & AdminCourtFields;

export interface CourtsFilters {
  q?: string;
  status?: AdminStatus;
  matchesMin?: number;
  matchesMax?: number;
  city?: string; // mocked for now
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface Pagination<T> {
  items: T[];
  total?: number; // optional until backend finalizes
  page: number;
  pageSize: number;
  pageCount?: number;
}


// Minimal shape based on Swagger example for GET /api/v2/courts
interface AdminListItemV2 {
  id: number;
  name: string;
  address: string;
  contactInfo?: string;
  matchesPlayed?: number;
  status?: AdminStatus;
  isActive?: boolean;
  isDraft?: boolean;
}

function toAdminCourt(item: AdminListItemV2): AdminCourt {
  const status: AdminStatus = (item.status as AdminStatus) || 'PENDING';
  // Use backend isActive explicitly; treat only true as active, false/null/undefined as inactive
  const isActive = (item as any).isActive === true;
  return {
    // CourtBigModel fields (fill missing with sensible defaults)
    id: item.id,
    name: item.name,
    address: item.address,
    latitude: 0,
    longitude: 0,
    type: [],
    matchesPlayed: item.matchesPlayed ?? 0,
    numberOfCourts: 0,
    bookingRequired: false,
    artificialLight: false,
    lockerRooms: false,
    coachAvailable: false,
    socialVibe: false,
    hasCafeOrBar: false,
    rentalGearAvailable: false,
    describeThisLocation: '',
    parkingAvailability: false,
    description: '',
    imagesUrls: [],
    avgRating: 0,
    websiteLink: '',
    // Admin fields
    isActive,
    adminStatus: status,
    activeUsersCount: 0,
    isDraft: !!(item as any).isDraft,
  };
}

export async function listCourts(filters: CourtsFilters): Promise<Pagination<AdminCourt>> {
  // Backend uses 0-based paging; convert from 1-based UI value
  const zeroBasedPage = Math.max(0, (filters.page ?? 1) - 1);
  const pageSize = filters.pageSize ?? 10;
  const query: Record<string, any> = {
    page: zeroBasedPage,
    size: pageSize,
  };
  if (filters.q) query.name = filters.q;
  if (filters.city) query.address = filters.city; // best-effort mapping; API supports address filter
  if (filters.status) query.status = filters.status;
  // API supports a single matchesPlayed filter; UI has min/max, so we only map exact if min==max
  if (
    typeof filters.matchesMin === 'number' &&
    typeof filters.matchesMax === 'number' &&
    filters.matchesMin === filters.matchesMax
  ) {
    query.matchesPlayed = filters.matchesMin;
  }

  const resp = await requestV2.get<any>(`/courts`, { query });

  const rawItems: any[] = Array.isArray(resp)
    ? resp
    : Array.isArray((resp as any)?.items)
      ? (resp as any).items
      : Array.isArray((resp as any)?.content)
        ? (resp as any).content
        : [];

  const items = rawItems.map((it) => toAdminCourt(it as AdminListItemV2));
  const total = (resp && (resp.total ?? resp.count ?? resp.totalElements)) as number | undefined;
  const backendPage = (resp && (resp.page ?? resp.pageNumber)) as number | undefined;
  const outPage = ((backendPage ?? zeroBasedPage) + 1);
  const outPageSize = (resp && (resp.pageSize ?? resp.size)) || pageSize;
  const pageCount = (resp && (resp.pageCount ?? resp.countPage ?? resp.totalPages)) as number | undefined;

  return { items, total, page: outPage, pageSize: outPageSize, pageCount };
}

export async function approveCourt(id: number, payload?: { reason?: string }): Promise<void> {
  await requestV2.post<void>(`/courts/${id}/approve`, { data: payload });
}

export async function rejectCourt(id: number, payload?: { reason?: string }): Promise<void> {
  // v2 uses 'decline'
  await requestV2.post<void>(`/courts/${id}/decline`, { data: payload });
}

export async function updateCourt(id: number, payload: Partial<AdminCourt>): Promise<void> {
  await request.patch<void>(`/courts/editCourt`, { data: payload, query: { courtId: id } });
}

export async function updateDraftCourt(id: number, payload: Partial<AdminCourt>): Promise<void> {
  await requestV2.put<void>(`/courts/draft/${id}`, { data: payload });
}

export async function toggleActive(id: number, isActive: boolean): Promise<void> {
  await requestV2.post<void>(`/courts/${id}/${isActive ? 'activate' : 'deactivate'}`, { data: {} });
}

export async function changeLogo(id: number, logoUrl: string): Promise<void> {
  // No dedicated logo endpoint in v2; replace the images with a single URL as a best effort.
  await updateCourtImages(id, [logoUrl]);
}

export async function updateCourtImages(id: number, imageUrls: string[]): Promise<void> {
  await requestV2.put<void>(`/courts/${id}/images`, { data: imageUrls });
}

export async function getCourtDetails(id: number): Promise<AdminCourt> {
  const data = await requestV2.get<any>(`/courts/${id}`);
  // Prefer fields as-is from v2 detail response, fallback to mapping
  const detail: AdminCourt = {
    id: data.id,
    name: data.name ?? '',
    address: data.address ?? '',
    latitude: data.latitude ?? 0,
    longitude: data.longitude ?? 0,
    type: Array.isArray(data.type) ? data.type : (typeof data.type === 'string' ? [data.type] : []),
    matchesPlayed: data.matchesPlayed ?? 0,
    numberOfCourts: data.numberOfCourts ?? 0,
    bookingRequired: !!data.bookingRequired,
    artificialLight: !!data.artificialLight,
    lockerRooms: !!data.lockerRooms,
    coachAvailable: !!data.coachAvailable,
    socialVibe: !!data.socialVibe,
    hasCafeOrBar: !!data.hasCafeOrBar,
    rentalGearAvailable: !!data.rentalGearAvailable,
    describeThisLocation: data.describeThisLocation ?? '',
    parkingAvailability: !!data.parkingAvailability,
    description: data.description ?? '',
    imagesUrls: Array.isArray(data.imagesUrls) ? data.imagesUrls : [],
    avgRating: data.avgRating ?? 0,
    websiteLink: data.websiteLink ?? '',
    // Admin fields (if present); fallback defaults
    isActive: !!data.isActive,
    adminStatus: (data.adminStatus as AdminStatus) ?? 'PENDING',
    activeUsersCount: data.activeUsersCount ?? 0,
    isDraft: !!data.isDraft,
  };
  return detail;
}

export async function getDraftCourtDetails(id: number): Promise<AdminCourt> {
  const data = await requestV2.get<any>(`/courts/draft/${id}`);
  const detail: AdminCourt = {
    id: data.id,
    name: data.name ?? '',
    address: data.address ?? '',
    latitude: data.latitude ?? 0,
    longitude: data.longitude ?? 0,
    type: Array.isArray(data.type) ? data.type : (typeof data.type === 'string' ? [data.type] : []),
    matchesPlayed: data.matchesPlayed ?? 0,
    numberOfCourts: data.numberOfCourts ?? 0,
    bookingRequired: !!data.bookingRequired,
    artificialLight: !!data.artificialLight,
    lockerRooms: !!data.lockerRooms,
    coachAvailable: !!data.coachAvailable,
    socialVibe: !!data.socialVibe,
    hasCafeOrBar: !!data.hasCafeOrBar,
    rentalGearAvailable: !!data.rentalGearAvailable,
    describeThisLocation: data.describeThisLocation ?? '',
    parkingAvailability: !!data.parkingAvailability,
    description: data.description ?? '',
    imagesUrls: Array.isArray(data.imagesUrls) ? data.imagesUrls : [],
    avgRating: data.avgRating ?? 0,
    websiteLink: data.websiteLink ?? '',
    isActive: !!data.isActive,
    adminStatus: (data.adminStatus as AdminStatus) ?? 'PENDING',
    activeUsersCount: data.activeUsersCount ?? 0,
    isDraft: true,
  };
  return detail;
}
